import React, { useContext, useRef, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { SEPARATOR } from 'enums'
import cx from 'classnames'
import { n, time2Pixel, getFPSByRefVideo, timelineTimeToSeconds, secondsToTimelineTime, nnn } from '~/Util'
import { getObjectTimeCode, isDropFrameTimeCode } from '~/Util/timecode'
import { TranslationContext } from '~/contexts/TranslationContext'
import { TIME_CODE_MODE, TIME_CODE_CONTAINER_LOCATION } from '~/config/constants/timecode'
import { getReferenceVideoAsset } from '~/selectors'
import { TimelineScrollPositionContext } from './ScrollPositionContext'
import { Context as TimelineGeometryContext } from './GeometryContextProvider'
import { SliderTimeContext } from '~/contexts/SliderTimeContext'
import './SliderPointer.scss'

function SliderPointer(props) {
  const { scale, refRuller } = props
  const pointerRef = useRef(null)
  const inputRef = useRef(null)

  // Берем только статические данные из контекстов (которые не меняются 60fps)
  const { scrollLeft, isSticky } = useContext(TimelineScrollPositionContext)
  const { timelineHeight } = useContext(TimelineGeometryContext)
  const { t } = useContext(TranslationContext)

  const { localTimeRef, subscribe } = useContext(SliderTimeContext)

  const refVideo = useSelector(getReferenceVideoAsset)
  const fps = getFPSByRefVideo(refVideo)
  const timeModes = useSelector(state => state.playback.timeCodeMode)
  const mode = timeModes[TIME_CODE_CONTAINER_LOCATION.TIMELINE]
  const isDF = isDropFrameTimeCode(mode, fps)

  const updatePointerDOM = useCallback(currentTime => {
    if (!pointerRef.current) return

    const timeCodeParts = getObjectTimeCode(timelineTimeToSeconds(currentTime), fps, mode)
    const { hours, minutes, seconds, milliseconds, frames, frameCount } = timeCodeParts

    let timeCodeDisplay
    if (mode === TIME_CODE_MODE.MILLISECOND) {
      timeCodeDisplay = `${n(hours)}:${n(minutes)}:${n(seconds)}${SEPARATOR.DOT}${nnn(milliseconds)}`
    } else {
      timeCodeDisplay = `${n(hours)}:${n(minutes)}:${n(seconds)}${isDF ? SEPARATOR.SEMICOLON : SEPARATOR.COLON}${n(frames)}`
    }

    const timeValue = mode === TIME_CODE_MODE.MILLISECOND
      ? currentTime
      : secondsToTimelineTime(frameCount / fps)

    const x = time2Pixel(timeValue, scale) - scrollLeft

    // 3. Прямая манипуляция стилями и атрибутами (обход Virtual DOM)
    pointerRef.current.style.transform = `translateX(${x}px)`

    if (refRuller.current) {
      pointerRef.current.style.display = refRuller.current.clientWidth < x ? 'none' : 'flex'
    }

    if (inputRef.current) {
      inputRef.current.value = timeCodeDisplay
    }
  }, [ fps, mode, isDF, scale, scrollLeft, refRuller ])


  useEffect(() => {
    updatePointerDOM(localTimeRef.current)
    const unsubscribe = subscribe(updatePointerDOM)
    return () => unsubscribe()
  }, [ subscribe, updatePointerDOM, localTimeRef ])

  return (
    <div
      ref={pointerRef}
      className="slider-pointer"
      data-lang-id="TIMELINE_SLIDER_POINTER"
      title={t('TIMELINE_SLIDER_POINTER_TOOLTIP')}
      style={{ willChange: 'transform' }} // Оптимизация композиции в браузере
    >
      <div className={cx('slider-pointer__head', {
        'slider-pointer__sticky': isSticky,
      })}
      />
      <input
        ref={inputRef}
        type="text"
        className="slider-pointer__input"
        aria-label="slider"
        readOnly
        defaultValue=""
      />
      <div
        className={cx('slider-pointer__body', {
          'slider-pointer__sticky': isSticky,
        })}
        style={{ height: timelineHeight }}
      />
    </div>
  )
}

SliderPointer.defaultProps = {
  refRuller: { current: null },
}

SliderPointer.propTypes = {
  refRuller: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  scale: PropTypes.number.isRequired,
}

export default React.memo(SliderPointer)
